/**
 * Library persistence helpers.
 *
 * Library data is stored in the user document under:
 *   users/{username}.orchestratorSavedData.library
 *
 * The shape is:
 *   {
 *     books: [{ id, title, coverUrl, content, pdfUrl, hidden }]
 *   }
 *
 * `hidden` controls whether the book is visible to players.
 * The session document gets a `library` field with only non-hidden books.
 */
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

const USERS_COLLECTION = 'users';

function sanitize(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function getUserRef(db, username) {
  return doc(db, USERS_COLLECTION, username);
}

/** Load the library for a user. Returns { books: [] } or null. */
export async function loadLibrary(db, username) {
  if (!username) return null;
  const snap = await getDoc(getUserRef(db, username));
  if (!snap.exists()) return null;
  const data = snap.data() || {};
  return data.orchestratorSavedData?.library || null;
}

/** Save the full library (all books) for a user. */
export async function saveLibrary(db, username, library) {
  if (!username) return;
  const payload = sanitize(library);
  await updateDoc(getUserRef(db, username), {
    'orchestratorSavedData.library': payload,
    'orchestratorSavedData.syncUpdatedAt': serverTimestamp(),
  });
}

/** Build the player-visible subset of library books. */
export function buildPlayerLibrary(library) {
  if (!library) return null;
  const books = (library.books || []).filter((b) => !b.hidden);
  return { books };
}
